import {
  VideoMeetingDisplayStatus,
  VideoMeetingGroup,
  VideoMeetingStatus,
  VideoMeetingType,
  VideoMeetingUserStatus,
} from '@/types/components';
import type {
  VideoMeeting,
  VideoMeetingAttendee,
  VideoMeetingAttendeeResponse,
  VideoMeetingDetailsResponse,
  VideoMeetingResponse,
} from '@/types/video';
import { formatLongDate, formatTime, isTodayInPeriod } from '@/utils/date';
import {
  getFullName,
  getInitials,
  joinNonEmpty,
  RANGE_SYMBOL,
  stripHtml,
} from '@/utils/text';

const { Confirmed, Pending, Declined, Canceled, None, Deleted } =
  VideoMeetingUserStatus;

const getDisplayStatus = ({
  meetingStatus,
  currentUserStatus,
}: VideoMeetingResponse): VideoMeetingDisplayStatus | null => {
  if (meetingStatus === VideoMeetingStatus.Ended) {
    return VideoMeetingDisplayStatus.Ended;
  }

  if (meetingStatus === VideoMeetingStatus.Deleted) {
    return VideoMeetingDisplayStatus.Canceled;
  }

  switch (currentUserStatus) {
    case Confirmed:
      return VideoMeetingDisplayStatus.Confirmed;
    case Pending:
      return VideoMeetingDisplayStatus.Pending;
    case Declined:
      return VideoMeetingDisplayStatus.Declined;
    case Canceled:
      return VideoMeetingDisplayStatus.Canceled;
    case None:
    case Deleted:
    case undefined:
      return null;
  }
};

const toVideoMeeting = (response: VideoMeetingResponse): VideoMeeting => {
  const { subject, timePeriod, organizer, currentUserStatus } = response;
  const isActive = response.meetingStatus === VideoMeetingStatus.Active;
  const hasNoInvitation = !currentUserStatus || currentUserStatus === None;
  const isLessonWithoutInvitation =
    response.type?.standardType === VideoMeetingType.Lesson && hasNoInvitation;

  return {
    id: response.id,
    group: isTodayInPeriod(timePeriod.from, timePeriod.to)
      ? VideoMeetingGroup.Today
      : VideoMeetingGroup.All,
    type: response.type?.standardType,
    displayStatus: getDisplayStatus(response),
    isActive,
    title: stripHtml(subject.text ?? ''),
    htmlBody: subject.text ?? '',
    authorName: getFullName(organizer),
    authorSubject: organizer.personType,
    date: formatLongDate(timePeriod.from),
    time: `${formatTime(timePeriod.from)} ${RANGE_SYMBOL} ${formatTime(timePeriod.to)}`,
    participantCount: response.attendantCount,
    url: response.url ?? '',
    isSingleSignOn: response.isSingleSignOn ?? false,
    canJoin: isActive && currentUserStatus === Confirmed,
    canJoinInDetails:
      isActive &&
      (currentUserStatus === Confirmed || isLessonWithoutInvitation),
    canApprove:
      isActive &&
      (currentUserStatus === Pending || currentUserStatus === Declined),
    canDecline:
      isActive &&
      currentUserStatus !== Declined &&
      currentUserStatus !== Confirmed,
    canDeclineInDetails:
      isActive && currentUserStatus !== Declined && !hasNoInvitation,
    attendees: [],
    attachments: subject.attachments ?? [],
  };
};

export const toVideoMeetings = (
  responses: VideoMeetingResponse[],
): VideoMeeting[] => responses.map(toVideoMeeting);

const toVideoMeetingAttendee = ({
  attendee,
  status,
}: VideoMeetingAttendeeResponse): VideoMeetingAttendee => ({
  id: attendee.id,
  name: getFullName(attendee),
  description: joinNonEmpty([attendee.personType, attendee.className], ' '),
  initials: getInitials(attendee),
  status,
});

export const toVideoMeetingDetails = (
  response: VideoMeetingDetailsResponse,
): VideoMeeting => ({
  ...toVideoMeeting(response),
  attendees: (response.attendees ?? []).map(toVideoMeetingAttendee),
});
